import { useEffect } from "react";
import {
  createRootRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { AppShell } from "../shell";
import { useSession } from "../lib/useSession";
import { useMeStore } from "../lib/user";
import { MeGate, type MeGateState } from "../features/auth/MeGate";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, ""); // "/app"

export const rootRoute = createRootRoute({
  component: function RootLayout() {
    const pathname = useRouterState({ select: (s) => s.location.pathname });
    const navigate = useNavigate();
    const session = useSession();
    const meStatus = useMeStore((s) => s.status);
    const meHttpStatus = useMeStore((s) => s.httpStatus);

    // Load the signed-in user's /v1/me once for the shell + account view.
    useEffect(() => {
      void useMeStore.getState().load();
    }, []);

    // No route renders unless /v1/me returned 200 (403 = no access).
    if (meStatus !== "ready") {
      let gateState: MeGateState = "loading";
      if (meStatus === "error") {
        gateState = meHttpStatus === 403 ? "forbidden" : "error";
      }
      return (
        <MeGate
          state={gateState}
          onRetry={() => void useMeStore.getState().load()}
          onSignOut={session.signOut}
        />
      );
    }

    // location.pathname may or may not include the router basepath; normalise
    // so nav/breadcrumb always compare against the public "/app/..." hrefs.
    const currentPath = pathname.startsWith(BASE)
      ? pathname
      : `${BASE}${pathname}`;

    return (
      <AppShell
        currentPath={currentPath}
        onSignOut={session.signOut}
        onNavigate={(href) =>
          navigate({
            to: href.startsWith(BASE) ? href.slice(BASE.length) || "/" : href,
          })
        }
      >
        <Outlet />
      </AppShell>
    );
  },
});
